import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { RISE_APPLY_SCORE_TIME_SERIES } from "@/api/queries/riseApplyScoreTimeSeries";
import { RISE_COMMIT_SCORE_TIME_SERIES } from "@/api/queries/riseCommitScoreTimeSeries";
import { RISE_MELT_SCORE_TIME_SERIES } from "@/api/queries/riseMeltScoreTimeSeries";
import { RISE_YIELD_SCORE_TIME_SERIES } from "@/api/queries/riseYieldScoreTimeSeries";

export type RisePredictiveIndexEnum =
  | "HIGH"
  | "MEDIUM"
  | "LOW"
  | "MEDIUM_HIGH"
  | "MEDIUM_LOW";
export type RisePredictiveModelEnum = "APPLY" | "COMMIT" | "MELT" | "YIELD";

export interface RiseTimeSeriesDataPoint {
  date: string;
  value: number;
  index?: RisePredictiveIndexEnum | null;
}

export interface UseRISEPredictiveScoreTimeSeriesReturn {
  timeSeries: RiseTimeSeriesDataPoint[];
  loading: boolean;
  error: unknown;
  hasData: boolean;
}

export const useRISEPredictiveScoreTimeSeries = (
  model: RisePredictiveModelEnum,
  orgId?: string,
  userId?: string,
): UseRISEPredictiveScoreTimeSeriesReturn => {
  const applyResult = useQuery(RISE_APPLY_SCORE_TIME_SERIES, {
    variables: { orgId: orgId || "", userId: userId || "" },
    skip: !orgId || !userId || model !== "APPLY",
  });

  const commitResult = useQuery(RISE_COMMIT_SCORE_TIME_SERIES, {
    variables: { orgId: orgId || "", userId: userId || "" },
    skip: !orgId || !userId || model !== "COMMIT",
  });

  const meltResult = useQuery(RISE_MELT_SCORE_TIME_SERIES, {
    variables: { orgId: orgId || "", userId: userId || "" },
    skip: !orgId || !userId || model !== "MELT",
  });

  const yieldResult = useQuery(RISE_YIELD_SCORE_TIME_SERIES, {
    variables: { orgId: orgId || "", userId: userId || "" },
    skip: !orgId || !userId || model !== "YIELD",
  });

  const activeResult = useMemo(() => {
    switch (model) {
      case "APPLY":
        return applyResult;
      case "COMMIT":
        return commitResult;
      case "MELT":
        return meltResult;
      case "YIELD":
        return yieldResult;
    }
  }, [model, applyResult, commitResult, meltResult, yieldResult]);

  const timeSeries = useMemo(() => {
    const { data } = activeResult;

    if (!data?.node || data.node.__typename !== "Student" || !data.node.rise) {
      return [] as RiseTimeSeriesDataPoint[];
    }

    const riseData = data.node.rise;
    let rawTimeSeriesData: {
      date: string;
      value: number;
      index?: RisePredictiveIndexEnum | null;
    }[] = [];

    const fieldMap = {
      APPLY: "applyScoreTimeSeries" as const,
      COMMIT: "commitScoreTimeSeries" as const,
      MELT: "meltScoreTimeSeries" as const,
      YIELD: "yieldScoreTimeSeries" as const,
    };

    const fieldName = fieldMap[model];
    rawTimeSeriesData =
      (riseData as Record<string, typeof rawTimeSeriesData>)[fieldName] || [];

    return rawTimeSeriesData.map((point) => ({
      date: point.date,
      value: point.value,
      index: point.index || null,
    }));
  }, [activeResult, model]);

  const hasData = timeSeries.length > 0;

  return {
    timeSeries,
    loading: activeResult.loading,
    error: activeResult.error,
    hasData,
  };
};
